/**
 * NSeComm.Knowledgebase.Main.js
 *
 * SC entry point. Three responsibilities:
 *   A. Inject a Knowledge Base block into category PLPs whose description
 *      HTML contains a #kb_articles container with .kb-entry rows.
 *   B. Inject a Knowledge Base block into item PDPs when the item has
 *      linked articles in the itemLinks map of /site/kb-data.json
 *      (published by the KB Admin Suitelet, action=publishjson).
 *   C. Register the ext-kb-list page type for /resources, /resources/topic/X,
 *      and /resources/:slug routes.
 *
 * All injection contexts use the same .kb-* classnames as the main /resources
 * page so a single SCSS file styles every context.
 */
define('NSeComm.Knowledgebase.Main', [
    'KB.List.View',
    'KB.Icons'
], function (
    KBListView,
    KBIcons
) {
    'use strict';

    // One kb-data.json fetch per page load, shared across PDP renders.
    var kbDataPromise = null;

    // Incremented per PDP render so a slow fetch can't inject a previous
    // item's articles after the shopper has navigated to another item.
    var pdpRenderSeq = 0;

    // Same guard for PLP renders (the category injection now waits on the
    // kb-data.json fetch, so it has the same stale-injection hazard).
    var plpRenderSeq = 0;

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // Plain-text excerpt from article HTML. Decodes entities via a detached
    // element so the result can be re-escaped without double-encoding.
    function excerptFromHtml(html, maxLen) {
        if (typeof html !== 'string' || !html) return '';
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
        if (text.length <= maxLen) return text;
        return text.substring(0, maxLen).trim() + '…';
    }

    // ─── Shared markup builders (category + item contexts) ───────────────

    function buildFaqItemHtml(questionHtml, answerHtml) {
        return '<div class="kb-faq-item">' +
                '<div class="kb-faq-question" onclick="this.parentElement.classList.toggle(\'kb-faq-open\')">' +
                    '<span class="kb-faq-icon"></span>' +
                    '<span>' + questionHtml + '</span>' +
                '</div>' +
                '<div class="kb-faq-answer">' + answerHtml + '</div>' +
            '</div>';
    }

    function buildArticleCardHtml(titleHtml, contentHtml, slug) {
        if (slug) {
            var arrowRight = KBIcons.get('arrowRight', 'kb-svg');
            return '<a href="/resources/' + encodeURIComponent(slug) + '" class="kb-article-card kb-article-card-link">' +
                    '<h3 class="kb-article-card-title">' + titleHtml + '</h3>' +
                    (contentHtml ? '<div class="kb-article-card-content">' + contentHtml + '</div>' : '') +
                    '<span class="kb-article-card-read">Read article ' + arrowRight + '</span>' +
                '</a>';
        }
        return '<div class="kb-article-card">' +
                '<h3 class="kb-article-card-title">' + titleHtml + '</h3>' +
                '<div class="kb-article-card-content">' + contentHtml + '</div>' +
            '</div>';
    }

    // opts.collapsible renders the title bar as a toggle (used on PLPs, where
    // the block sits above the product grid and defaults to collapsed so it
    // never pushes products down); opts.count labels the collapsed bar.
    function buildKbSectionHtml(faqHtml, articleHtml, extraClass, opts) {
        opts = opts || {};
        var html = '<div class="kb-category-section' + (extraClass ? ' ' + extraClass : '') +
            (opts.collapsible ? ' kb-collapsed' : '') + '">';
        if (opts.collapsible) {
            html += '<h2 class="kb-category-section-title" role="button" tabindex="0" aria-expanded="false"' +
                ' onclick="var s=this.parentElement;s.classList.toggle(\'kb-collapsed\');' +
                'this.setAttribute(\'aria-expanded\',s.classList.contains(\'kb-collapsed\')?\'false\':\'true\');"' +
                ' onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();this.click();}">' +
                'Knowledge Base' +
                (opts.count ? '<span class="kb-category-section-count">' + opts.count +
                    (opts.count === 1 ? ' entry' : ' entries') + '</span>' : '') +
                '<span class="kb-category-section-toggle" aria-hidden="true"></span>' +
                '</h2>';
        } else {
            html += '<h2 class="kb-category-section-title">Knowledge Base</h2>';
        }

        if (faqHtml) {
            html += '<h3 class="kb-subsection-title">Frequently Asked Questions</h3>';
            html += '<div class="kb-faq-section">' + faqHtml + '</div>';
        }

        if (articleHtml) {
            html += '<h3 class="kb-subsection-title">Related Articles</h3>';
            html += '<div class="kb-articles-section">' + articleHtml + '</div>';
        }

        html += '</div>';
        return html;
    }

    // Older suitelet syncs stripped the hidden #kb_articles block with a lazy
    // regex and stranded tails of article content (bare <ul>/<p> fragments)
    // in the category description, where the theme renders them as if they
    // were real description copy. Until every category is re-synced with the
    // fixed suitelet, hide any rendered block whose text is a verbatim chunk
    // of a published article.
    function scrubDescriptionDebris(articles) {
        var header = document.querySelector('.facets-browse-category-heading-list-header') ||
            document.querySelector('.facets-facet-browse-category-heading-container');
        if (!header) return;

        var norm = function (s) { return String(s || '').replace(/\s+/g, ' ').trim(); };

        var haystacks = [];
        (articles || []).forEach(function (a) {
            if (!a || !a.content) return;
            var tmp = document.createElement('div');
            tmp.innerHTML = a.content;
            var t = norm(tmp.textContent || tmp.innerText);
            if (t.length >= 20) haystacks.push(t);
        });
        if (!haystacks.length) return;

        // Only content blocks — never divs (the category title lives in one).
        var nodes = header.querySelectorAll('ul, ol, p, blockquote, table');
        for (var i = 0; i < nodes.length; i++) {
            var el = nodes[i];
            if (el.closest && el.closest('#kb_articles')) continue; // hidden anyway
            var text = norm(el.textContent);
            if (text.length < 20) continue;
            for (var j = 0; j < haystacks.length; j++) {
                if (haystacks[j].indexOf(text) !== -1) {
                    el.style.display = 'none';
                    break;
                }
            }
        }
    }

    // ─── A. Category section — built from .kb-entry rows that the KB Admin
    //        Suitelet syncs into the category description ─────────────────

    function buildCategorySection(entries) {
        var faqHtml = '';
        var articleHtml = '';

        for (var i = 0; i < entries.length; i++) {
            var entry = entries[i];
            var type = entry.getAttribute('data-type');

            if (type === 'faq') {
                var qNode = entry.querySelector('.kb-question');
                var aNode = entry.querySelector('.kb-answer');
                faqHtml += buildFaqItemHtml(
                    qNode ? qNode.innerHTML : '',
                    aNode ? aNode.innerHTML : ''
                );
            } else {
                var tNode = entry.querySelector('.kb-title');
                var cNode = entry.querySelector('.kb-content');
                articleHtml += buildArticleCardHtml(
                    tNode ? tNode.innerHTML : '',
                    cNode ? cNode.innerHTML : '',
                    entry.getAttribute('data-slug') || ''
                );
            }
        }

        return buildKbSectionHtml(faqHtml, articleHtml, 'kb-plp-top', {
            collapsible: true,
            count: entries.length
        });
    }

    // ─── B. Item (PDP) section — built from kb-data.json ─────────────────

    function loadKbData() {
        if (!kbDataPromise) {
            kbDataPromise = jQuery.ajax({
                url: '/site/kb-data.json',
                dataType: 'json',
                cache: false
            }).then(function (data) {
                data = data || {};
                if (!data.articles) data.articles = [];
                if (!data.itemLinks) data.itemLinks = {};
                return data;
            });
            // Don't cache a failure — retry on the next PDP render.
            kbDataPromise.fail(function () { kbDataPromise = null; });
        }
        return kbDataPromise;
    }

    function extractItemId(info) {
        if (!info) return null;
        return info._id || info.internalid || info.internalId || info.id ||
            (info.item && (info.item.internalid || info.item._id || info.item.id)) || null;
    }

    function buildItemSection(articleIds, articles) {
        var byId = {};
        (articles || []).forEach(function (a) {
            if (a && a.id != null) byId[String(a.id)] = a;
        });

        var faqHtml = '';
        var articleHtml = '';

        (articleIds || []).forEach(function (id) {
            var a = byId[String(id)];
            if (!a) return; // link points at an article that isn't live

            if (a.typeId === '1') {
                // FAQ — full question/answer accordion, collapsed by default.
                // question is plain text in kb-data.json; content is HTML.
                faqHtml += buildFaqItemHtml(escapeHtml(a.question || a.title), a.content || '');
            } else if (a.slug) {
                // Long-form article with its own /resources page — keep the
                // PDP light with an excerpt card that links out.
                var excerpt = excerptFromHtml(a.content, 180);
                articleHtml += buildArticleCardHtml(
                    escapeHtml(a.title),
                    excerpt ? '<p>' + escapeHtml(excerpt) + '</p>' : '',
                    a.slug
                );
            } else {
                // No detail page to link to — show the full content inline,
                // same as the category treatment.
                articleHtml += buildArticleCardHtml(escapeHtml(a.title), a.content || '', '');
            }
        });

        if (!faqHtml && !articleHtml) return '';
        return buildKbSectionHtml(faqHtml, articleHtml, 'kb-item-section');
    }

    function injectItemSection(sectionHtml) {
        var notInQuickView = function () {
            return !jQuery(this).closest('.product-details-quick-view, .quick-view-modal, .modal-content').length;
        };

        // Append at the end of the outermost PDP container so the block
        // stacks full-width below the gallery / buy box / description tabs.
        // Do NOT target .product-details-information first: on this theme
        // that's the buy-box column (CustomPackages hides price/options
        // inside it), and inserting a sibling there would land the block
        // inside the two-column wrapper, next to the image gallery. It's
        // kept only as a last-resort anchor since it's proven to exist.
        var $container = jQuery('.product-details-full-content').filter(notInQuickView).first();
        if (!$container.length) $container = jQuery('.product-details-full').filter(notInQuickView).first();
        if (!$container.length) $container = jQuery('.product-details-content').filter(notInQuickView).first();
        if ($container.length) {
            $container.append(sectionHtml);
            return;
        }

        var $buyBox = jQuery('.product-details-information').filter(notInQuickView).first();
        if ($buyBox.length) $buyBox.after(sectionHtml);
    }

    function handlePdpShow(pdp) {
        var seq = ++pdpRenderSeq;

        var info = pdp.getItemInfo ? pdp.getItemInfo() :
            (pdp.getProductInfo ? pdp.getProductInfo() : null);
        var itemId = extractItemId(info);
        if (!itemId && window.SC && window.SC.itemInfo) itemId = window.SC.itemInfo.id;
        if (!itemId) return;

        loadKbData().done(function (data) {
            if (seq !== pdpRenderSeq) return; // shopper moved on to another item

            jQuery('.kb-item-section').remove();

            var linkedIds = data.itemLinks[String(itemId)];
            if (!linkedIds || !linkedIds.length) return;

            var sectionHtml = buildItemSection(linkedIds, data.articles);
            if (sectionHtml) injectItemSection(sectionHtml);
        });
    }

    return {
        mountToApp: function mountToApp(container) {
            var plp = container.getComponent('PLP');
            var pdp = container.getComponent('PDP');
            var pageTypeComponent = container.getComponent('PageType');

            // ─── A. Category Page Injection ───
            if (plp) {
                plp.cancelableOn('afterShowContent', function () {
                    var seq = ++plpRenderSeq;
                    setTimeout(function () {
                        if (seq !== plpRenderSeq) return; // shopper moved on

                        jQuery('.kb-category-section').not('.kb-item-section').remove();

                        var catinfo = window.SC && window.SC.categoryinfo;
                        var descHtml = catinfo && catinfo.description;
                        if (!descHtml) return;

                        var tempDiv = document.createElement('div');
                        tempDiv.innerHTML = descHtml;
                        var kbDiv = tempDiv.querySelector('#kb_articles');

                        // Publish gate: the synced description can lag the
                        // article records (it only updates on Sync Categories),
                        // so cross-check each entry against kb-data.json — the
                        // Publish JSON output that contains ONLY articles that
                        // are published AND reviewed. Unknown slug → not live
                        // → don't show it.
                        loadKbData().done(function (data) {
                            if (seq !== plpRenderSeq) return; // shopper moved on

                            scrubDescriptionDebris(data.articles);

                            if (!kbDiv) return;
                            var entries = kbDiv.querySelectorAll('.kb-entry');
                            if (!entries.length) return;

                            var liveSlugs = {};
                            (data.articles || []).forEach(function (a) {
                                if (a && a.slug) liveSlugs[String(a.slug)] = true;
                            });

                            var liveEntries = [];
                            for (var i = 0; i < entries.length; i++) {
                                var slug = entries[i].getAttribute('data-slug');
                                if (slug && liveSlugs[slug]) liveEntries.push(entries[i]);
                            }
                            if (!liveEntries.length) return;

                            var sectionHtml = buildCategorySection(liveEntries);

                            // Top of the PLP, right under the category heading
                            // (collapsed by default), so infinite scroll can't
                            // bury it below an ever-growing product grid. The
                            // heading lives inside the theme's floated results
                            // container, so inserting after it keeps the block
                            // in normal flow above the grid.
                            jQuery('.kb-category-section').not('.kb-item-section').remove();
                            var target = jQuery('.facets-facet-browse-category-heading-container');
                            if (target.length) {
                                target.first().after(sectionHtml);
                                return;
                            }
                            target = jQuery('.facets-facet-browse-results');
                            if (target.length) target.first().before(sectionHtml);
                        });
                    }, 600);
                });
            }

            // ─── B. Item Page (PDP) Injection ───
            if (pdp && pdp.on) {
                pdp.on('afterShowContent', function () {
                    try {
                        handlePdpShow(pdp);
                    } catch (e) {
                        if (window.console && console.error) {
                            console.error('Knowledgebase PDP error', e);
                        }
                    }
                });
            }

            // ─── C. Page Type Registration ───
            if (pageTypeComponent) {
                pageTypeComponent.registerPageType({
                    name: 'ext-kb-list',
                    routes: [
                        'resources(/)',
                        'resources?*queryParams',
                        'resources/topic/:topicSlug(/)',
                        'resources/:slug(/)',
                        'academy/resources(/)',
                        'academy/resources?*queryParams',
                        'academy/resources/topic/:topicSlug(/)',
                        'academy/resources/:slug(/)'
                    ],
                    view: KBListView,
                    defaultTemplate: {
                        name: 'kb_list_page.tpl',
                        displayName: 'KB Article List'
                    }
                });
            }
        }
    };
});
